import type { IAddon, IData, IDataPoint, IItem, TDescriptionTable } from 'Components/sq-graphics/interfaces';
import { chop_intend, hfmse, hine_mm, mfm, nsaa, rulm, Doc_Progress } from 'neuro-schemas';
import { exists, isNumber, isPartialDate } from 'neuro-utils';

import { getICD10Description } from 'Utility/randomUtil';
import { capitalize } from 'Utility/string';

const { calculateScore: hfmsEScore } = hfmse;

const { calculateScore: hineMMScore } = hine_mm;

const { calculateScore: chopINTENDScore } = chop_intend;

const { calculateScore: mfmScore, calculateProgress: calculateMFMProgress } = mfm;

const { calculateScore: nsaaScore } = nsaa;

const {
  utils: { rulmSuperiorHand },
  calculateScore: rulmScore,
} = rulm;

const isMfmComplete = (doc: IDocumentWithTypeV2<'mfm'>): boolean =>
  calculateMFMProgress(doc).state === Doc_Progress.State.Completed;

export const convertNsaaToGraph = (
  docs: IDocumentWithTypeV2<'nsaa'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const score = nsaaScore(d);
    if (!isNumber(score)) return;
    const value = Number(score);
    dataPoints.push({
      date: dateFromPartialUpdateTimeframe(d.date),
      value,
      id: 'nsaa',
      title: value.toString(),
    });
  });
  return [
    {
      dataPoints,
      id: 'nsaa',
      type: 'lineGraph',
      legend: 'NSAA',
    },
  ];
};

export const convertMfmToGraph = (
  docs: IDocumentWithTypeV2<'mfm'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
  type: 'mfm20' | 'mfm32',
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  // Filter docs based on the type variable that tells the type of the data we want to return
  const filteredDocs = docs.filter(d => {
    if (d.mfm32OrMfm20 === type) return true;
    return false;
  });
  filteredDocs.forEach(d => {
    // this isMfmComplete considers also manual score fields
    if (!isPartialDate(d.date) || !isMfmComplete(d)) return;
    const scores = mfmScore(d);
    if (!scores) return;
    const value = scores.total;
    const description: TDescriptionTable = [];
    Object.keys(scores).forEach(k => {
      const key = k as keyof typeof scores;
      if (key === 'total') return;
      description.push({ title: fm(`motor.opts.mfm.${k}`), values: scores[key] });
    });
    dataPoints.push({
      date: dateFromPartialUpdateTimeframe(d.date),
      value,
      id: type,
      title: `${fm('motor.opts.mfm.total')}: ${scores.total}`,
      description,
    });
  });

  return [
    {
      dataPoints,
      id: type,
      type: 'lineGraph',
      legend: type === 'mfm20' ? 'MFM 20' : 'MFM 32',
    },
  ];
};

export const convert6mwtToGraph = (
  docs: IDocumentWithTypeV2<'mwt'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string, values?: { [key: string]: string }) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    isNumber(d.meters) &&
      dataPoints.push({
        date: dateFromPartialUpdateTimeframe(d.date),
        value: d.meters,
        id: '6mwt',
        title: fm('motor.meterValue', { value: d.meters.toString() }),
      });
  });
  return [
    {
      dataPoints,
      id: '6mwt',
      type: 'lineGraph',
      legend: '6MWT',
    },
  ];
};

export const convertChopIntendToGraph = (
  docs: IDocumentWithTypeV2<'chop_intend'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const score = chopINTENDScore(d);
    if (!isNumber(score)) return;
    const value = Number(score);
    dataPoints.push({
      date: dateFromPartialUpdateTimeframe(d.date),
      value,
      id: 'chopIntend',
      title: value.toString(),
    });
  });
  return [
    {
      dataPoints,
      id: 'chopIntend',
      type: 'lineGraph',
      legend: 'CHOP INTEND',
    },
  ];
};

export const convertHinemmToGraph = (
  docs: IDocumentWithTypeV2<'hine_mm'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const score = hineMMScore(d);
    if (!isNumber(score)) return;
    const value = Number(score);
    dataPoints.push({
      date: dateFromPartialUpdateTimeframe(d.date),
      value,
      id: 'hinemm',
      title: value.toString(),
    });
  });
  return [
    {
      dataPoints,
      id: 'hinemm',
      type: 'lineGraph',
      legend: 'HINE MM',
    },
  ];
};

export const convertHfmseToGraph = (
  docs: IDocumentWithTypeV2<'hfms_e'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const score = hfmsEScore(d);
    if (!isNumber(score)) return;
    const value = Number(score);
    dataPoints.push({
      date: dateFromPartialUpdateTimeframe(d.date),
      value,
      id: 'hfmse',
      title: value.toString(),
    });
  });
  return [
    {
      dataPoints,
      id: 'hfmse',
      type: 'lineGraph',
      legend: 'HFMS-E',
    },
  ];
};

export const convertRulmToGraph = (
  docs: IDocumentWithTypeV2<'rulm'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const superiorHand = rulmSuperiorHand(d);
    if (!superiorHand) return;
    const score = rulmScore(d, superiorHand);
    if (!isNumber(score)) return;
    const value = Number(score);
    const description = fm('motor.' + superiorHand.toLocaleLowerCase());
    dataPoints.push({
      date: dateFromPartialUpdateTimeframe(d.date),
      value,
      id: 'rulm',
      title: value.toString(),
      description,
    });
  });
  return [
    {
      dataPoints,
      id: 'rulm',
      type: 'lineGraph',
      legend: 'RULM',
    },
  ];
};

const motorFunctionSections = [
  'sittingWithoutSupport',
  'crawlingOnHandsAndKnees',
  'standingWithAssistance',
  'standingWithoutAssistance',
  'walkingWithAssistance',
  'walkingWithoutAssistance',
];

export const convertMotorFunctionToTimeline = (
  docs: IDocumentWithTypeV2<'motorfunction'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): IAddon[] | undefined => {
  if (docs.length === 0) return undefined;
  const addons: Array<IAddon> = [];
  docs.forEach(doc => {
    const functionsCareItems: IAddon[] = [];
    motorFunctionSections.forEach(s => {
      const startDate = doc[(s + 'Start') as keyof typeof doc];
      const endDate = doc[(s + 'End') as keyof typeof doc];
      if (isPartialDate(startDate)) {
        const items: IItem[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(startDate),
          end: isPartialDate(endDate) ? dateFromPartialUpdateTimeframe(endDate) : undefined,
          title: fm('motorFunctionAndMobilityAid.motorfunction.' + s),
        });
        functionsCareItems.push({
          id: s,
          title: fm('motorFunctionAndMobilityAid.motorfunction.' + s),
          titleDescription: undefined,
          items,
          events: [],
        });
      }
    });
    addons.push(...functionsCareItems);
  });
  return addons.length > 0 ? addons : undefined;
};

/** Entries of `ventilationAid` to make addon descriptions from. */
const addonDescriptionVentilationEntries: Array<keyof IDocumentWithTypeV2<'ventilationAid'>> = [
  'type',
  'frequency',
  'hoursPerDay',
];

export const convertVentilationUseToTimeline = (
  docs: IDocumentWithTypeV2<'ventilationAid'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string, values?: { [key: string]: string }) => string,
): IAddon[] | undefined => {
  if (docs.length === 0) return undefined;
  const addons: Array<IAddon> = [];
  docs.forEach(doc => {
    if (!isPartialDate(doc.startDate)) return undefined;
    const items: IItem[] = [];
    items.push({
      start: dateFromPartialUpdateTimeframe(doc.startDate),
      end: isPartialDate(doc.endDate) ? dateFromPartialUpdateTimeframe(doc.endDate) : undefined,
      title: fm('pulmonary.ventilation'),
      description: addonDescriptionVentilationEntries.reduce<TDescriptionTable>((acc, cur) => {
        if (cur === 'hoursPerDay') {
          acc.push({
            title: fm('pulmonary.hoursPerDayLowercase', { value: doc.hoursPerDay?.toString() ?? '' }),
            condition: exists(doc.hoursPerDay),
          });
        } else if (doc[cur]) {
          acc.push({
            title: fm(`pulmonary.opts.${doc[cur]}`),
            condition: exists(doc[cur]),
          });
        }
        return acc;
      }, []),
    });
    addons.push({
      id: 'ventilation',
      title: fm('pulmonary.ventilation'),
      titleDescription: undefined,
      items,
      events: [],
    });

    return;
  });
  return addons.length > 0 ? addons : undefined;
};

export const convertNutritionToTimeline = (
  docs: IDocumentWithTypeV2<'nutrition'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): IAddon[] | undefined => {
  if (docs.length === 0) return undefined;
  const addons: Array<IAddon> = [];
  docs.forEach(doc => {
    const feedingTubes = doc.feedingTubes?.filter(f => isPartialDate(f.startDate));
    if (feedingTubes && feedingTubes.length > 0) {
      const items: IItem[] = [];
      feedingTubes.forEach(f => {
        f.startDate &&
          items.push({
            start: dateFromPartialUpdateTimeframe(f.startDate),
            end: f.endDate ? dateFromPartialUpdateTimeframe(f.endDate) : undefined,
            title: fm('nutrition.feedingTube'),
            description: ['feedingTubeType', 'feedingTubePractice'].map(name => {
              return {
                title: exists(f[name as keyof typeof f]) ? fm(`nutrition.opts.${f[name as keyof typeof f]}`) : '',
                condition: exists(f[name as keyof typeof f]),
              };
            }),
          });
      });
      addons.push({
        id: 'feedingTube',
        title: fm('nutrition.feedingTube'),
        titleDescription: undefined,
        items,
        events: [],
      });
    }
  });
  return addons.length > 0 ? addons : undefined;
};

export const convertMobilityAidToTimeline = (
  docs: IDocumentWithTypeV2<'mobilityAid'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): IAddon[] | undefined => {
  if (docs.length === 0) return undefined;
  const addons: Array<IAddon> = [];
  docs.forEach(doc => {
    if (!isPartialDate(doc.startDate)) return undefined;
    const description: TDescriptionTable = ['type', 'use'].reduce<TDescriptionTable>((acc, cur) => {
      const val = doc[cur as keyof typeof doc];
      if (val)
        acc.push({
          title: fm(`motorFunctionAndMobilityAid.aids.opts.${val}`),
          condition: exists(val),
        });
      return acc;
    }, []);
    switch (doc.aidType) {
      case 'wheelChair': {
        const items: IItem[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(doc.startDate),
          end: isPartialDate(doc.endDate) ? dateFromPartialUpdateTimeframe(doc.endDate) : undefined,
          title: fm('motorFunctionAndMobilityAid.aids.wheelChairTitle'),
          description,
        });
        addons.push({
          id: 'wheelChair',
          title: fm('motorFunctionAndMobilityAid.aids.wheelChairTitle'),
          titleDescription: undefined,
          items,
          events: [],
        });
        break;
      }
      case 'walkingStand': {
        const items: IItem[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(doc.startDate),
          end: isPartialDate(doc.endDate) ? dateFromPartialUpdateTimeframe(doc.endDate) : undefined,
          title: fm('motorFunctionAndMobilityAid.aids.walkingStandTitle'),
          description,
        });
        addons.push({
          id: 'walkingStand',
          title: fm('motorFunctionAndMobilityAid.aids.walkingStandTitle'),
          titleDescription: undefined,
          items,
          events: [],
        });
        break;
      }
      case 'crutches': {
        const items: IItem[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(doc.startDate),
          end: isPartialDate(doc.endDate) ? dateFromPartialUpdateTimeframe(doc.endDate) : undefined,
          title: fm('motorFunctionAndMobilityAid.aids.crutchesTitle'),
          description,
        });
        addons.push({
          id: 'crutches',
          title: fm('motorFunctionAndMobilityAid.aids.crutchesTitle'),
          titleDescription: undefined,
          items,
          events: [],
        });
        break;
      }
      default: {
        break;
      }
    }

    return;
  });
  return addons.length > 0 ? addons : undefined;
};

export const convertHospitalizationToTimeline = (
  docs: IDocumentWithTypeV2<'hospitalization'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
  uiLang: Locale,
): IAddon[] | undefined => {
  if (docs.length === 0) return undefined;
  const addons: Array<IAddon> = [];
  docs.forEach(doc => {
    const description = (icd10code?: string, description?: string) => [
      {
        title: fm('comorbidity.hospitalizationType'),
        values: doc.hospitalizationType ? fm(`comorbidity.opts.${doc.hospitalizationType}`) : undefined,
      },
      {
        title: fm('comorbidity.acuteReason'),
        values: icd10code ? `${doc.classification?.icd10code} - ${description}` : undefined,
        condition: doc.hospitalizationType === 'acute',
      },
      {
        title: fm(`comorbidity.acuteAdverse${capitalize(doc.acuteAdverseEffect ?? '')}`),
        condition: exists(doc.acuteAdverseEffect),
      },
      {
        title: fm('medication.medication'),
        values: doc.acuteAdverseEffectMedication,
        condition: doc.acuteAdverseEffect === 'yes',
      },
      {
        title: fm('comorbidity.details'),
        values: doc.hospitalizationDetails,
        condition: exists(doc.hospitalizationDetails),
      },
    ];
    if (!isPartialDate(doc.date)) return undefined;
    const items: IItem[] = [];
    items.push({
      start: dateFromPartialUpdateTimeframe(doc.date),
      end: isPartialDate(doc.endDate) ? dateFromPartialUpdateTimeframe(doc.endDate) : undefined,
      title: fm('comorbidity.hospitalization'),
      description: doc.classification?.icd10code
        ? getICD10Description(doc.classification.icd10code, uiLang).then(v =>
            description(doc.classification?.icd10code, v),
          )
        : description(),
    });
    addons.push({
      id: 'hospitalization',
      title: fm('comorbidity.hospitalization'),
      titleDescription: undefined,
      items,
      events: [],
    });

    return;
  });
  return addons.length > 0 ? addons : undefined;
};
